/**
 * Mobile navigation
 * =================
 * Toggles the slide-in-from-top panel and animates the hamburger button.
 *
 * Markup contract (see header.php):
 *   button#site-navigation-toggle.menu-toggle[aria-controls="site-navigation"]
 *   nav#site-navigation.main-navigation
 */

const BODY_CLASS = 'nav-open';
// Must match the SCSS `@include bp-down(md)` boundary, which in this grid
// resolves to "below the `lg` breakpoint" (64em / 1024px).
const DESKTOP_QUERY = '(min-width: 64em)';

function initMobileNav() {
	const toggle = document.getElementById('site-navigation-toggle');
	const nav = document.getElementById('site-navigation');

	if (!toggle || !nav) {
		return;
	}

	const desktop = window.matchMedia(DESKTOP_QUERY);
	let lastFocused = null;

	// Keep the panel's top padding in sync with the real header height
	// so the logo stays visible while the menu is open.
	const header = toggle.closest('.site-header');

	const syncHeaderHeight = () => {
		if (!header) {
			return;
		}

		document.documentElement.style.setProperty(
			'--db-header-height',
			`${Math.round(header.getBoundingClientRect().height)}px`
		);
	};

	syncHeaderHeight();
	window.addEventListener('resize', syncHeaderHeight, { passive: true });

	if ('ResizeObserver' in window && header) {
		new ResizeObserver(syncHeaderHeight).observe(header);
	}

	const isOpen = () => toggle.getAttribute('aria-expanded') === 'true';

	const setLabel = (open) => {
		toggle.setAttribute(
			'aria-label',
			open
				? toggle.dataset.labelClose || 'Menu sluiten'
				: toggle.dataset.labelOpen || 'Menu openen'
		);
	};

	const openNav = () => {
		lastFocused = document.activeElement;
		toggle.setAttribute('aria-expanded', 'true');
		document.body.classList.add(BODY_CLASS);
		setLabel(true);
	};

	const closeNav = ({ restoreFocus = false } = {}) => {
		toggle.setAttribute('aria-expanded', 'false');
		document.body.classList.remove(BODY_CLASS);
		setLabel(false);

		if (restoreFocus) {
			(lastFocused instanceof HTMLElement ? lastFocused : toggle).focus();
		}
	};

	// ── Toggle ───────────────────────────────────────────────────────────────
	toggle.addEventListener('click', () => {
		if (isOpen()) {
			closeNav();
		} else {
			openNav();
		}
	});

	// ── Close on link click (also handles in-page anchors) ───────────────────
	nav.addEventListener('click', (event) => {
		if (event.target.closest('a') && isOpen()) {
			closeNav();
		}
	});

	// ── Close on Escape ──────────────────────────────────────────────────────
	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && isOpen()) {
			closeNav({ restoreFocus: true });
		}
	});

	// ── Close when clicking outside the panel ────────────────────────────────
	document.addEventListener('click', (event) => {
		if (!isOpen()) {
			return;
		}

		if (!nav.contains(event.target) && !toggle.contains(event.target)) {
			closeNav();
		}
	});

	// ── Simple focus trap while open ─────────────────────────────────────────
	nav.addEventListener('keydown', (event) => {
		if (event.key !== 'Tab' || !isOpen()) {
			return;
		}

		const focusable = [
			toggle,
			...nav.querySelectorAll('a[href], button:not([disabled])'),
		];

		if (!focusable.length) {
			return;
		}

		const first = focusable[0];
		const last = focusable[focusable.length - 1];

		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	});

	// ── Reset when resizing up to desktop ────────────────────────────────────
	const handleBreakpoint = (event) => {
		if (event.matches && isOpen()) {
			closeNav();
		}
	};

	if (typeof desktop.addEventListener === 'function') {
		desktop.addEventListener('change', handleBreakpoint);
	} else if (typeof desktop.addListener === 'function') {
		desktop.addListener(handleBreakpoint); // Safari < 14
	}

	// Ensure a clean initial state
	closeNav();
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initMobileNav);
} else {
	initMobileNav();
}
